import React from 'react';
import { object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { selectListingCollection } from '../../ducks/listingCollections.duck';
import { categoryPageSchema } from '../../util/landingSchema';
import { cantonsWithPage } from '../../util/categoryRegions';
import { regionSlug } from '../../util/regions';

import { NamedLink } from '../../components';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import LandingLayout from './LandingLayout';
import { CATEGORIES, STEPS } from './landingContent';
import categoriesData from './data/categories.json';

import Hero from './sections/Hero';
import ClaimBanner from './sections/ClaimBanner';
import Usps from './sections/Usps';
import ListingGrid from './sections/ListingGrid';
import Explainer from './sections/Explainer';
import InfoCards from './sections/InfoCards';
import CardGrid from './sections/CardGrid';
import Steps from './sections/Steps';
import Faq from './sections/Faq';
import TrustBanner from './sections/TrustBanner';
import Testimonials from './sections/Testimonials';
import FinalCta from './sections/FinalCta';

const categoryBySlug = slug => CATEGORIES.find(c => c.slug === slug) || null;

export const CategoryLandingPageComponent = props => {
  const { params, listings, scrollingDisabled } = props;
  const slug = params?.slug;
  const category = categoryBySlug(slug);
  const copy = categoriesData.categories?.[slug];

  if (!category || !copy) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  // searchHref is a full path in the data; NamedLink builds the pathname from
  // the route table and takes only the query.
  const searchLink = { name: 'SearchPage', to: { search: copy.searchHref.replace(/^[^?]*/, '') } };

  return (
    <LandingLayout
      title={copy.seoTitle}
      description={copy.metaDescription}
      schema={categoryPageSchema({ ...copy, shortLabel: category.label })}
      currentPage="CategoryLandingPage"
      scrollingDisabled={scrollingDisabled}
    >
      <Hero
        crumbs={[
          { label: 'Startseite', link: { name: 'LandingPage' } },
          { label: 'Kategorien', link: { name: 'CategoryHubPage' } },
          { label: category.label },
        ]}
        heading={copy.h1}
        lead={copy.intro}
        tagline={copy.heroTagline}
        primaryCta={{ link: searchLink, label: 'Jetzt Angebote entdecken' }}
        secondaryCta={{ link: { href: '#faq' }, label: 'Häufige Fragen' }}
      />

      <ClaimBanner />
      <Usps />

      <ListingGrid
        listings={listings}
        heading={`Beliebte ${category.label} Angebote`}
        text="Aktuelle Angebote direkt vergleichen und buchen."
        emptyState={
          <>
            <p className="listings-empty">
              {`Für ${category.label} sind aktuell keine Angebote ausgeschrieben. Schaut in den anderen Kategorien oder durchsucht alle Angebote.`}
            </p>
            <div style={{ textAlign: 'center', marginTop: '40px' }}>
              <NamedLink className="btn btn-ghost" name="SearchPage">
                Alle Angebote ansehen
              </NamedLink>
            </div>
          </>
        }
        footerLink={{ link: searchLink, label: 'Alle Angebote ansehen' }}
      />

      <CardGrid
        id="cantons-title"
        heading={`${category.label} nach Kanton`}
        text="Anbieter:innen, die in eurem Kanton arbeiten."
        alt
        items={cantonsWithPage(category.slug).map(canton => ({
          key: canton.option,
          link: {
            name: 'CategoryRegionLandingPage',
            params: { slug: category.slug, region: regionSlug(canton.option) },
          },
          label: canton.label,
        }))}
      />

      <Explainer
        heading={copy.explainer?.heading}
        text={copy.explainer?.text}
        items={copy.explainer?.checklist}
      />

      <Steps heading={STEPS.heading} text={STEPS.text} items={copy.steps?.items || STEPS.items} />

      <InfoCards heading={copy.info?.heading} text={copy.info?.text} cards={copy.info?.cards} />

      <Faq heading={copy.faq?.heading || 'Häufige Fragen'} items={copy.faq?.items} />

      <CardGrid
        id="other-cats-title"
        heading="Weitere Kategorien"
        text="Alles, was eure Hochzeit braucht – an einem Ort."
        items={CATEGORIES.map(cat => ({
          key: cat.slug,
          link: { name: 'CategoryLandingPage', params: { slug: cat.slug } },
          label: cat.label,
          image: cat.image,
        }))}
      />

      <TrustBanner />
      <Testimonials />

      <FinalCta
        heading={copy.finalCta?.heading || copy.h1}
        text={copy.finalCta?.text || copy.intro}
        link={searchLink}
        label="Jetzt Angebote entdecken"
      />
    </LandingLayout>
  );
};

CategoryLandingPageComponent.propTypes = { params: object };

const mapStateToProps = (state, ownProps) => {
  const slug = ownProps?.params?.slug;
  const { listingIds } = selectListingCollection(state, `category-${slug}`);
  return {
    listings: getListingsById(state, listingIds),
    scrollingDisabled: isScrollingDisabled(state),
  };
};

export default compose(connect(mapStateToProps))(CategoryLandingPageComponent);
